import type { Account, AccountType } from '@zeni/shared'
import type { TranslationKey } from '../i18n/translations'

/** 口座種別の表示名キー。口座画面と振替UIで共通に使う */
export const ACCOUNT_TYPE_KEYS: Record<AccountType, TranslationKey> = {
  checking: 'account_type_checking',
  savings: 'account_type_savings',
  credit_card: 'account_type_credit_card',
  investment: 'account_type_investment',
  cash: 'account_type_cash',
  other: 'account_type_other',
}

/**
 * 振替の向きを判断するための口座の役割。
 * 普通預金と定期預金は振替の文脈では同じ「銀行口座」として扱う。
 */
export type AccountRole = 'bank' | 'card' | 'cash' | 'investment' | 'other'

/** 役割そのものの表示名キー。出金元の口座が複数あって種別まで一意に決まらないときに使う */
export const ACCOUNT_ROLE_KEYS: Record<AccountRole, TranslationKey> = {
  bank: 'account_type_bank_group',
  card: 'account_type_credit_card',
  cash: 'account_type_cash',
  investment: 'account_type_investment',
  other: 'account_type_other',
}

export function accountRole(type: AccountType): AccountRole {
  switch (type) {
    case 'checking':
    case 'savings':
      return 'bank'
    case 'credit_card':
      return 'card'
    case 'cash':
      return 'cash'
    case 'investment':
      return 'investment'
    default:
      return 'other'
  }
}

/**
 * 出金元の役割ごとに、振替先として自然な役割を「よく使う順」で並べたもの。
 * ここに含まれない組み合わせは UI 上「一般的でない」として注意を出す。
 *
 * 例: 銀行口座から引く振替はカードの引き落としが最も多く、
 * クレジットカードから引く振替は銀行への返金・キャッシングしかない。
 */
const RECOMMENDED_ROLES: Record<AccountRole, AccountRole[]> = {
  bank: ['card', 'bank', 'investment', 'cash'],
  card: ['bank'],
  cash: ['bank', 'investment'],
  investment: ['bank', 'cash'],
  // 種別が「その他」の口座は用途を推測できないため全部おすすめに出す
  other: ['bank', 'card', 'cash', 'investment', 'other'],
}

export function recommendedRoles(fromRole: AccountRole): AccountRole[] {
  return RECOMMENDED_ROLES[fromRole]
}

/** 出金元と振替先の組み合わせが表す、家計簿上の意味 */
export type TransferPattern =
  | 'card_payment'
  | 'between_banks'
  | 'withdrawal'
  | 'deposit'
  | 'investing'
  | 'divestment'
  | 'cash_advance'
  | 'generic'
  | 'unusual'

export function transferPattern(fromRole: AccountRole, toRole: AccountRole): TransferPattern {
  if (fromRole === 'other' || toRole === 'other') return 'generic'
  if (toRole === 'card') {
    return fromRole === 'bank' || fromRole === 'cash' ? 'card_payment' : 'unusual'
  }
  if (fromRole === 'card') return toRole === 'bank' || toRole === 'cash' ? 'cash_advance' : 'unusual'
  if (fromRole === 'bank' && toRole === 'bank') return 'between_banks'
  if (fromRole === 'bank' && toRole === 'cash') return 'withdrawal'
  if (fromRole === 'cash' && toRole === 'bank') return 'deposit'
  if (toRole === 'investment') return 'investing'
  if (fromRole === 'investment') return 'divestment'
  return 'unusual'
}

export function transferPatternKeys(pattern: TransferPattern): { label: TranslationKey; desc: TranslationKey } {
  switch (pattern) {
    case 'card_payment':
      return { label: 'transfer_pattern_card_payment', desc: 'transfer_pattern_card_payment_desc' }
    case 'between_banks':
      return { label: 'transfer_pattern_between_banks', desc: 'transfer_pattern_between_banks_desc' }
    case 'withdrawal':
      return { label: 'transfer_pattern_withdrawal', desc: 'transfer_pattern_withdrawal_desc' }
    case 'deposit':
      return { label: 'transfer_pattern_deposit', desc: 'transfer_pattern_deposit_desc' }
    case 'investing':
      return { label: 'transfer_pattern_investing', desc: 'transfer_pattern_investing_desc' }
    case 'divestment':
      return { label: 'transfer_pattern_divestment', desc: 'transfer_pattern_divestment_desc' }
    case 'cash_advance':
      return { label: 'transfer_pattern_cash_advance', desc: 'transfer_pattern_cash_advance_desc' }
    case 'unusual':
      return { label: 'transfer_pattern_unusual', desc: 'transfer_pattern_unusual_desc' }
    default:
      return { label: 'transfer_pattern_generic', desc: 'transfer_pattern_generic_desc' }
  }
}

/** 振替先セレクトの optgroup ひとつ分 */
export interface TransferTargetGroup {
  role: AccountRole
  /** 出金元との組み合わせが表す意味。optgroup のラベルに使う */
  pattern: TransferPattern
  accounts: Account[]
}

export interface TransferTargets {
  /** 出金元の種別から見て自然な振替先。よく使う順に並ぶ */
  recommended: TransferTargetGroup[]
  /** おすすめに入らなかった残りの口座 */
  others: Account[]
}

/**
 * 振替先の候補を「おすすめ」と「その他」に振り分ける。
 *
 * 出金元が未確定（fromRole が null）のときは絞り込みようがないので、
 * すべてを others として返し、呼び出し側で通常のリストとして出す。
 */
export function transferTargets(
  accounts: Account[],
  fromRole: AccountRole | null,
  excludeIds: string[] = [],
): TransferTargets {
  const excluded = new Set(excludeIds)
  const selectable = accounts.filter((a) => !a.isArchived && !excluded.has(a.id))
  if (!fromRole) return { recommended: [], others: selectable }

  const recommended: TransferTargetGroup[] = []
  const used = new Set<string>()
  for (const role of recommendedRoles(fromRole)) {
    const group = selectable.filter((a) => accountRole(a.type) === role)
    if (group.length === 0) continue
    group.forEach((a) => used.add(a.id))
    recommended.push({ role, pattern: transferPattern(fromRole, role), accounts: group })
  }
  return { recommended, others: selectable.filter((a) => !used.has(a.id)) }
}
